import { isEmpty } from "lodash-es";
import { useCallback } from "react";
import { Container, Row } from "reactstrap";
import { Toolbar } from "../../../components/Toolbar";
import { humanizeString } from "../../../util/formatters";
import { AdministrateBackgroundExportButton } from "../../components/AdministrateBackgroundExportButton";
import { AdministrateEnumFilter } from "../../components/AdministrateEnumFilter";
import { AdministrateTable } from "../../components/AdministrateTable";
import { PayorOptionsFilter } from "../../components/filters/PayorOptionsFilter";
import { SystemConfigLayout } from "../../components/SystemConfigLayout";
import { DESTROY, makeColumns, UPDATE, useAdministrateTable, useCreatePermission } from "../../utils";
import { AddPhoneResponseModal } from "./components/AddPhoneResponseModal";
import { ImportPhoneResponseModal } from "./components/ImportPhoneResponseModal";
import { PhoneResponseActionCell } from "./components/PhoneResponseActionCell";

const PayorNamesDisplayer = ({ payors }) => {
  return (
    <>
      {payors &&
        payors.map((payor) => (
          <li style={{ listStyle: "none" }} className="token" key={payor.id}>
            <a target="_blank" rel="noopener noreferrer" href={`/admin/system/payors/${payor.id}/general`}>
              {payor.name}
            </a>
          </li>
        ))}
      {isEmpty(payors) && (
        <li style={{ listStyle: "none" }} className="token">
          ALL PAYORS
        </li>
      )}
    </>
  );
};

const statusesColors = {
  // PA Statuses:
  approved: "success",
  denied: "danger",
  duplicate: "info",
  completed: "success",
  held: "info",
  not_required: "info",
  documented_clean_claim: "info",
  processing: "info",
  received: "info",
  rejected: "danger",
  missinginfo: "warning",
  aim_not_required: "info",
  beacon_not_required: "info",
  submitted: "info",
  medicalreview: "warning",
  partially_approved: "info",
  code_specific_result: "info",
  preprocessing: "warning",
  // BI Statuses (processing is both a PA and BI status):
  open: "primary",
  closed: "info",
  // 'none' is passed from parent components if a status cannot be determined:
  none: "secondary",
};

export const StatusDisplayer = ({ status }) => {
  const color = statusesColors[status?.toLowerCase()];
  let selectedStatus;
  // External users will not have the preprocessing status listed in window.pa_statuses, and it should
  // instead be treated like 'received':
  if (status === "preprocessing" && status in window.pa_statuses === false) {
    selectedStatus = window.pa_statuses["received"];
  } else {
    selectedStatus = window.pa_statuses[status] || window.bi_statuses[status];
  }

  return (
    <span title={selectedStatus?.name} className={`badge-${color} badge text-xs`}>
      <small>{humanizeString(selectedStatus?.name || "None")}</small>
    </span>
  );
};

export const SubstatusDisplayer = ({ substatus }) => {
  const color = substatus === "none" ? "secondary" : "info";
  const substatusString = window.pa_substatuses[substatus];

  return (
    <span title={substatusString || "None"} className={`badge-${color} badge`}>
      <small>{substatusString || "None"}</small>
    </span>
  );
};

const useColumns = makeColumns(() => [
  {
    id: "message",
    accessor: "message",
    Header: "Message",
  },
  {
    id: "submit_right_direction",
    accessor: (row) => {
      switch (row.submitRightDirection) {
        case "status_the_case":
          return "Status The Case";
        case "task_out_for_n_number_of_days_to_try_again":
          return "Task Out For N Number of Days To Try Again";
        case "proceed_to_the_next_submission":
          return "Proceed To The Next Submission";
      }
    },
    Header: "Submit Right Direction",
    Filter: (props) => (
      <AdministrateEnumFilter
        {...props}
        resourceUrl={"/admin/enums/phone_response_submit_right_direction_options.json"}
        valueKey="id"
        labelKey="label"
      />
    ),
  },
  {
    id: "payors",
    accessor: "payors",
    Header: "Verified Payor Name",
    Filter: (props) => <PayorOptionsFilter {...props} isMulti />,
    Cell: (props) => <PayorNamesDisplayer payors={props.value} />,
  },
  {
    id: "status",
    accessor: "status",
    Header: "Status",
    Filter: (props) => (
      <AdministrateEnumFilter {...props} resourceUrl="/admin/enums/prior_auth_statuses.json" valueKey="id" labelKey="label" />
    ),
    Cell: (props) => <StatusDisplayer status={props.value || "none"} />,
  },
  {
    id: "substatus",
    accessor: "substatus",
    Header: "Substatus",
    Filter: (props) => (
      <AdministrateEnumFilter {...props} resourceUrl="/admin/enums/prior_auth_substatuses.json" valueKey="value" labelKey="label" />
    ),
    Cell: (props) => <SubstatusDisplayer substatus={props.value || "none"} />,
  },
  {
    id: "canned_comment",
    accessor: "cannedComment",
    Header: "Canned Comment",
  },
  {
    id: "actions",
    accessor: "Actions",
    Header: "Actions",
    Cell: PhoneResponseActionCell,
    disableFilters: true,
    disableSortBy: true,
  },
]);

export const PhoneResponsesIndex = () => {
  const { tableRef, allFilters, setTableFilters } = useAdministrateTable();
  const columns = useColumns();
  const [canCreate] = useCreatePermission("PhoneResponse");

  const onAdd = useCallback(
    (message, errorDetails = []) => {
      tableRef.current?.refresh?.();
      tableRef.current?.setMessage?.(message, errorDetails.length ? "warning" : "success", errorDetails);
    },
    [tableRef]
  );

  return (
    <SystemConfigLayout title="Phone Responses">
      <Container fluid>
        <Row className="align-items-center mb-3">
          <Toolbar className="ml-auto">
            {canCreate && (
              <AdministrateBackgroundExportButton
                outline
                className="me-2"
                exportUrl="/admin/phone_responses/export"
                filters={allFilters}
                ref={tableRef}
              />
            )}

            {canCreate && <ImportPhoneResponseModal outline className="me-2" onImport={onAdd} />}
            {canCreate && <AddPhoneResponseModal onAdd={onAdd} color="primary" />}
          </Toolbar>
        </Row>
      </Container>

      <AdministrateTable
        resourceUrl={"/admin/phone_responses.json"}
        resourceName={"phone_response"}
        permissions={[DESTROY, UPDATE]}
        onFilteredChange={setTableFilters}
        columns={columns}
        ref={tableRef}
      />
    </SystemConfigLayout>
  );
};
